import { useRef, useState, useCallback, useEffect } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { motion } from 'framer-motion'
import type { Card } from '../types'
import { CARD_MIN_SIZES, GRID_COLS } from '../types'
import { useCardStore } from '../store/cardStore'
import { useThemeStore } from '../store/themeStore'
import CardRenderer from './cards/CardRenderer'
import { sfx } from '../utils/sfx'

function hashToUint32(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

interface DraggableCardProps {
  card: Card
  layoutCellSize: number
  pointerCellSize: number
  editMode: boolean
  isDragging: boolean
}

export default function DraggableCard({ card, layoutCellSize, pointerCellSize, editMode, isDragging }: DraggableCardProps) {
  const { setSelectedCard, updateCardSize, updateCardRect, saveCardPositions, checkOverlap } = useCardStore()
  const { theme, paletteSeed, colorPalette, cardRadius } = useThemeStore()
  const [isResizing, setIsResizing] = useState(false)
  const startSizeRef = useRef({ width: card.grid_width, height: card.grid_height })
  const startRectRef = useRef({ x: card.grid_x, y: card.grid_y, width: card.grid_width, height: card.grid_height })
  const startPosRef = useRef({ x: 0, y: 0 })
  const lastTransformRef = useRef<{ x: number; y: number } | null>(null)
  const lastResizeStepRef = useRef<{ width: number; height: number } | null>(null)

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: card.id,
    disabled: !editMode || isResizing,
  })

  const minSize = CARD_MIN_SIZES[card.card_type]

  const handleResizeStart = useCallback((
    e: React.MouseEvent | React.TouchEvent,
    direction:
      | 'right'
      | 'left'
      | 'bottom'
      | 'top'
      | 'bottom_right'
      | 'bottom_left'
      | 'top_right'
      | 'top_left'
  ) => {
    e.preventDefault()
    e.stopPropagation()
    setIsResizing(true)
    startSizeRef.current = { width: card.grid_width, height: card.grid_height }
    startRectRef.current = { x: card.grid_x, y: card.grid_y, width: card.grid_width, height: card.grid_height }
    lastResizeStepRef.current = { width: card.grid_width, height: card.grid_height }

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    startPosRef.current = { x: clientX, y: clientY }

    const bodyPrevUserSelect = document.body.style.userSelect
    const bodyPrevWebkitUserSelect = (document.body.style as any).webkitUserSelect
    const bodyPrevOverflow = document.body.style.overflow
    document.body.style.userSelect = 'none'
      ; (document.body.style as any).webkitUserSelect = 'none'
    document.body.style.overflow = 'hidden'

    let rafId: number | null = null
    let pendingX: number | null = null
    let pendingY: number | null = null

    const applyResize = () => {
      rafId = null
      if (pendingX === null || pendingY === null) return

      const deltaX = Math.round((pendingX - startPosRef.current.x) / pointerCellSize)
      const deltaY = Math.round((pendingY - startPosRef.current.y) / pointerCellSize)

      const start = startRectRef.current

      let nextX = start.x
      let nextY = start.y
      let nextW = start.width
      let nextH = start.height

      const affectsRight = direction === 'right' || direction === 'bottom_right' || direction === 'top_right'
      const affectsLeft = direction === 'left' || direction === 'bottom_left' || direction === 'top_left'
      const affectsBottom = direction === 'bottom' || direction === 'bottom_right' || direction === 'bottom_left'
      const affectsTop = direction === 'top' || direction === 'top_right' || direction === 'top_left'

      if (affectsRight) {
        nextW = Math.max(minSize.width, start.width + deltaX)
        nextW = Math.min(nextW, GRID_COLS - start.x)
      }

      if (affectsBottom) {
        nextH = Math.max(minSize.height, start.height + deltaY)
      }

      if (affectsLeft) {
        // Moving left edge changes both x and width.
        const rawW = start.width - deltaX
        const clampedW = Math.max(minSize.width, rawW)
        const appliedDelta = start.width - clampedW
        nextX = start.x + appliedDelta
        nextW = clampedW
        if (nextX < 0) {
          nextW = nextW + nextX
          nextX = 0
          nextW = Math.max(minSize.width, nextW)
        }
      }

      if (affectsTop) {
        // Moving top edge changes both y and height.
        const rawH = start.height - deltaY
        const clampedH = Math.max(minSize.height, rawH)
        const appliedDelta = start.height - clampedH
        nextY = start.y + appliedDelta
        nextH = clampedH
        if (nextY < 0) {
          nextH = nextH + nextY
          nextY = 0
          nextH = Math.max(minSize.height, nextH)
        }
      }

      // Make sure we don't exceed grid width
      if (nextX + nextW > GRID_COLS) {
        nextW = GRID_COLS - nextX
      }

      if (!checkOverlap(card.id, nextX, nextY, nextW, nextH)) {
        const last = lastResizeStepRef.current
        const changed = !last || last.width !== nextW || last.height !== nextH
        if (changed) {
          const stepDiff = last ? Math.abs(nextW - last.width) + Math.abs(nextH - last.height) : 1
          const needsRectUpdate = affectsLeft || affectsTop
          if (needsRectUpdate) {
            updateCardRect(card.id, nextX, nextY, nextW, nextH)
          } else {
            updateCardSize(card.id, nextW, nextH)
          }
          sfx.resizeSteps(stepDiff)
          lastResizeStepRef.current = { width: nextW, height: nextH }
        }
      }
    }

    const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
      if ('touches' in moveEvent) moveEvent.preventDefault()

      const currentX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX
      const currentY = 'touches' in moveEvent ? moveEvent.touches[0].clientY : moveEvent.clientY

      pendingX = currentX
      pendingY = currentY

      if (rafId == null) rafId = window.requestAnimationFrame(applyResize)
    }

    const handleEnd = () => {
      setIsResizing(false)
      saveCardPositions()
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleEnd)
      document.removeEventListener('touchmove', handleMove)
      document.removeEventListener('touchend', handleEnd)
      if (rafId != null) window.cancelAnimationFrame(rafId)
      document.body.style.userSelect = bodyPrevUserSelect
        ; (document.body.style as any).webkitUserSelect = bodyPrevWebkitUserSelect
      document.body.style.overflow = bodyPrevOverflow
    }

    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleEnd)
    // On mobile browsers, touchmove on document may be passive by default.
    document.addEventListener('touchmove', handleMove, { passive: false })
    document.addEventListener('touchend', handleEnd)
  }, [card, pointerCellSize, minSize, updateCardSize, updateCardRect, saveCardPositions, checkOverlap])

  useEffect(() => {
    if (!transform) {
      lastTransformRef.current = null
      return
    }

    const prev = lastTransformRef.current
    if (!prev) {
      lastTransformRef.current = { x: transform.x, y: transform.y }
      return
    }

    const dx = transform.x - prev.x
    const dy = transform.y - prev.y
    if (Math.abs(dx) + Math.abs(dy) >= 2) {
      sfx.dragStep()
      lastTransformRef.current = { x: transform.x, y: transform.y }
    }
  }, [transform])

  const liftPx = isDragging ? 3 : 0
  const snappedTransform = transform
    ? {
      x: Math.round(transform.x),
      y: Math.round(transform.y),
    }
    : null
  const transformStyle = transform
    ? `translate3d(${snappedTransform!.x}px, ${snappedTransform!.y}px, 0) translateY(${-liftPx}px)`
    : liftPx
      ? `translateY(${-liftPx}px)`
      : undefined

  const forcedPaletteIndexRaw = (card.content as any)?.paletteIndex
  const forcedPaletteIndex =
    forcedPaletteIndexRaw === 1 || forcedPaletteIndexRaw === 2 || forcedPaletteIndexRaw === 3 || forcedPaletteIndexRaw === 4
      ? forcedPaletteIndexRaw
      : null
  const paletteIndex = forcedPaletteIndex ?? ((hashToUint32(`${card.id}-${paletteSeed}`) % 4) + 1)
  const paletteBg = `var(--card-bg-${paletteIndex})`

  // Calculate foreground color based on theme and palette
  let paletteFg = '#0a0a0a'
  if (theme === 'neo') {
    // Neo palette foreground depends on colorPalette selection
    if (colorPalette === 0) {
      paletteFg = paletteIndex === 1 ? '#e8f5ee' : '#011910'
    } else if (colorPalette === 1) {
      paletteFg = paletteIndex === 1 ? '#e0e8f5' : '#0a0f1e'
    } else if (colorPalette === 2) {
      paletteFg = paletteIndex === 1 ? '#f0e8f5' : '#1a0a20'
    } else {
      paletteFg = paletteIndex === 1 ? '#f5e8ef' : '#1a0a14'
    }
  } else if (theme === 'glass') {
    paletteFg = 'rgba(10,10,10,0.92)'
  } else if (theme === 'wood') {
    if (colorPalette === 1) {
      paletteFg = paletteIndex === 4 ? 'rgba(243,231,213,0.96)' : 'rgba(41,40,49,0.96)'
    } else if (colorPalette === 2) {
      paletteFg = paletteIndex >= 3 ? 'rgba(16,31,38,0.96)' : 'rgba(232,249,244,0.96)'
    } else {
      paletteFg = 'rgba(243,231,213,0.96)'
    }
  }

  const cardPadding = cardRadius === 'sharp' ? 1 : 4;
  const cardRadiusCSS = cardRadius === 'sharp' ? '0px' : '16px';

  const style: Record<string, any> = {
    position: 'absolute' as const,
    left: card.grid_x * layoutCellSize,
    top: card.grid_y * layoutCellSize,
    width: card.grid_width * layoutCellSize,
    height: card.grid_height * layoutCellSize,
    padding: cardPadding,
    transform: transformStyle,
    zIndex: isDragging ? 1000 : card.z_index,
    opacity: 1,
    boxShadow: isDragging ? '0 14px 38px rgba(0,0,0,0.1)' : '0 10px 30px rgba(0,0,0,0)',
    ['--card-bg']: paletteBg,
    ['--card-fg']: paletteFg,
    ['--card-radius']: cardRadiusCSS,
    // Prevent touch scroll on individual cards in edit mode
    touchAction: editMode ? 'none' : 'auto',
    // GPU compositing for smooth drag
    willChange: isDragging ? 'transform' : 'auto',
  }


  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group ${isDragging ? 'card-dragging' : ''}`}
      onDoubleClick={(e) => {
        if (!editMode) return
        e.stopPropagation()
        setSelectedCard(card.id)
      }}
      {...attributes}
      {...listeners}
    >
      <motion.div
        className="w-full h-full relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15, ease: 'linear' }}
      >
        <div className="w-full h-full relative">
          <CardRenderer card={card} editMode={editMode} cellSize={layoutCellSize} />
        </div>
      </motion.div>

      {/* Edit Button */}
      {editMode && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            setSelectedCard(card.id)
          }}
          className="absolute top-1 right-1 p-1.5 bg-[var(--card-bg,var(--card-bg-1))] text-[var(--card-fg)] opacity-0 group-hover:opacity-100 z-10"
        >
          <img src="/sett.svg" alt="" className="w-3.5 h-3.5 block" draggable={false} />
        </button>
      )}

      {/* Resize Handles */}
      {editMode && (
        <>
          {/* Left edge */}
          <div
            className="resize-handle resize-handle-left"
            onMouseDown={(e) => handleResizeStart(e, 'left')}
            onTouchStart={(e) => handleResizeStart(e, 'left')}
          />
          {/* Right edge */}
          <div
            className="resize-handle resize-handle-right"
            onMouseDown={(e) => handleResizeStart(e, 'right')}
            onTouchStart={(e) => handleResizeStart(e, 'right')}
          />
          {/* Top edge */}
          <div
            className="resize-handle resize-handle-top"
            onMouseDown={(e) => handleResizeStart(e, 'top')}
            onTouchStart={(e) => handleResizeStart(e, 'top')}
          />
          {/* Bottom edge */}
          <div
            className="resize-handle resize-handle-bottom"
            onMouseDown={(e) => handleResizeStart(e, 'bottom')}
            onTouchStart={(e) => handleResizeStart(e, 'bottom')}
          />
          {/* Top-left corner */}
          <div
            className="resize-handle resize-handle-top-left"
            onMouseDown={(e) => handleResizeStart(e, 'top_left')}
            onTouchStart={(e) => handleResizeStart(e, 'top_left')}
          />
          {/* Top-right corner */}
          <div
            className="resize-handle resize-handle-top-right"
            onMouseDown={(e) => handleResizeStart(e, 'top_right')}
            onTouchStart={(e) => handleResizeStart(e, 'top_right')}
          />
          {/* Bottom-left corner */}
          <div
            className="resize-handle resize-handle-bottom-left"
            onMouseDown={(e) => handleResizeStart(e, 'bottom_left')}
            onTouchStart={(e) => handleResizeStart(e, 'bottom_left')}
          />
          {/* Bottom-right corner */}
          <div
            className="resize-handle resize-handle-bottom-right"
            onMouseDown={(e) => handleResizeStart(e, 'bottom_right')}
            onTouchStart={(e) => handleResizeStart(e, 'bottom_right')}
          />
        </>
      )}
    </div>
  )
}
